import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  AddShoppingCart as AddToCartIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';
import { useTranslation } from 'react-i18next';

export interface StorageItemViewItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  quantity: number;
  storeQuantity?: number;
  takenQuantity?: number;
  minQuantity: number;
  unit: string;
  location: string;
  supplier?: string;
  barkodas?: string;
  prekesKodas?: string;
  fabisKodas?: string;
  savikaina?: number;
  pardavimoKaina?: number;
  imageUrl?: string | null;
  creator?: { firstName: string; lastName: string };
  createdAt?: string;
  updatedAt?: string;
}

export type ItemCartTransferMode = 'warehouse' | 'store' | 'taken';

interface StorageItemViewProps<T extends StorageItemViewItem = StorageItemViewItem> {
  items: T[];
  viewMode: 'cards' | 'list';
  selectedQuantities: { [key: string]: number };
  onQuantityChange: (itemId: string, quantity: number) => void;
  onAddToCart: (item: T) => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  canEdit: boolean;
  emptyMessage: string;
  showStoreQuantity?: boolean;
  showTakenQuantity?: boolean;
  cartTransferMode?: ItemCartTransferMode;
}

function StorageItemView<T extends StorageItemViewItem>({
  items,
  viewMode,
  selectedQuantities,
  onQuantityChange,
  onAddToCart,
  onEdit,
  onDelete,
  canEdit,
  emptyMessage,
  showStoreQuantity = false,
  showTakenQuantity = false,
  cartTransferMode = 'warehouse'
}: StorageItemViewProps<T>) {
  const { t } = useTranslation();
  const getCartMax = (item: T) => {
    if (showStoreQuantity && cartTransferMode === 'store') {
      return item.storeQuantity ?? 0;
    }
    if (showTakenQuantity && cartTransferMode === 'taken') {
      return item.takenQuantity ?? 0;
    }
    return item.quantity;
  };

  const getCartDisabled = (item: T) => getCartMax(item) === 0;

  const getAvailableForMinCheck = (item: T) =>
    item.quantity + (item.storeQuantity ?? 0);

  const isLowStock = (item: T) =>
    getAvailableForMinCheck(item) <= item.minQuantity;

  const isInStore = (item: T) =>
    showStoreQuantity && (item.storeQuantity ?? 0) > 0;

  const isTaken = (item: T) =>
    showTakenQuantity && (item.takenQuantity ?? 0) > 0;

  const renderPBadge = (size: 'card' | 'list', tooltip: string, ariaLabel: string) => (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size === 'card' ? 40 : 28,
          height: size === 'card' ? 40 : 28,
          borderRadius: size === 'card' ? '0 0 0 12px' : '50%',
          backgroundColor: '#7b1fa2',
          color: '#fff',
          fontWeight: 800,
          fontSize: size === 'card' ? '1.5rem' : '0.95rem',
          lineHeight: 1,
          boxShadow: size === 'card' ? 2 : 1,
          flexShrink: 0
        }}
        aria-label={ariaLabel}
      >
        P
      </Box>
    </Tooltip>
  );
  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  if (viewMode === 'list') {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pavadinimas</TableCell>
              {showStoreQuantity ? (
                <>
                  <TableCell align="right">Sandėlyje</TableCell>
                  <TableCell align="right">Parduotuvėje</TableCell>
                </>
              ) : showTakenQuantity ? (
                <>
                  <TableCell align="right">Sandėlyje</TableCell>
                  <TableCell align="right">Paimta</TableCell>
                </>
              ) : (
                <TableCell align="right">Kiekis</TableCell>
              )}
              <TableCell>Vieta</TableCell>
              <TableCell>Tiekėjas</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>Kiekis</TableCell>
              <TableCell align="right">Veiksmai</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const lowStock = isLowStock(item);
              const inStore = isInStore(item);
              const taken = isTaken(item);
              const badgeTooltip = inStore
                ? t('storage.souvenirs.badgeInStore', 'Yra parduotuvėje')
                : t('storage.workshop.badgeTaken', 'Paimta');
              const badgeAria = inStore ? 'Parduotuvėje' : 'Paimta';
              return (
                <TableRow
                  key={item.id}
                  sx={{
                    backgroundColor: lowStock ? '#ffebee' : undefined
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: lowStock ? '#d32f2f' : '#212121'
                        }}
                      >
                        {item.name}
                      </Typography>
                      {inStore && renderPBadge('list', badgeTooltip, badgeAria)}
                      {taken && renderPBadge('list', badgeTooltip, badgeAria)}
                      {lowStock && (
                        <Tooltip title="Reikia papildyti">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                    {item.description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280, display: 'block' }}>
                        {item.description}
                      </Typography>
                    )}
                  </TableCell>
                  {showStoreQuantity ? (
                    <>
                      <TableCell align="right">
                        <Chip
                          label={`${item.quantity} ${item.unit}`}
                          color={lowStock ? 'error' : 'default'}
                          variant={lowStock ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${item.storeQuantity ?? 0} ${item.unit}`}
                          color="primary"
                          variant={inStore ? 'filled' : 'outlined'}
                          size="small"
                          sx={inStore ? { fontWeight: 600 } : undefined}
                        />
                      </TableCell>
                    </>
                  ) : showTakenQuantity ? (
                    <>
                      <TableCell align="right">
                        <Chip
                          label={`${item.quantity} ${item.unit}`}
                          color={lowStock ? 'error' : 'default'}
                          variant={lowStock ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${item.takenQuantity ?? 0} ${item.unit}`}
                          color="primary"
                          variant={taken ? 'filled' : 'outlined'}
                          size="small"
                          sx={taken ? { fontWeight: 600 } : undefined}
                        />
                      </TableCell>
                    </>
                  ) : (
                  <TableCell align="right">
                    <Chip
                      label={`${item.quantity} ${item.unit}`}
                      color={lowStock ? 'error' : 'default'}
                      variant={lowStock ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </TableCell>
                  )}
                  <TableCell>{item.location || '—'}</TableCell>
                  <TableCell>{item.supplier || '—'}</TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={selectedQuantities[item.id] || 1}
                      onChange={(e) => onQuantityChange(item.id, parseInt(e.target.value) || 1)}
                      inputProps={{
                        min: 1,
                        max: getCartMax(item),
                        style: { textAlign: 'center' }
                      }}
                      sx={{ width: 72 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddToCartIcon />}
                        onClick={() => onAddToCart(item)}
                        disabled={getCartDisabled(item)}
                        sx={{
                          backgroundColor: '#7b1fa2',
                          '&:hover': { backgroundColor: '#6a1b9a' }
                        }}
                      >
                        Į krepšelį
                      </Button>
                      {canEdit && (
                        <>
                          <Tooltip title="Redaguoti prekę ir kiekį">
                            <IconButton size="small" onClick={() => onEdit(item)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ištrinti prekę">
                            <IconButton size="small" onClick={() => onDelete(item)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Cards mode
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 3
      }}
    >
      {items.map((item) => {
        const lowStock = isLowStock(item);
        const inStore = isInStore(item);
        const taken = isTaken(item);
        const badgeTooltip = inStore
          ? t('storage.souvenirs.badgeInStore', 'Yra parduotuvėje')
          : t('storage.workshop.badgeTaken', 'Paimta');
        const badgeAria = inStore ? 'Parduotuvėje' : 'Paimta';
        return (
          <Card
            key={item.id}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              border: lowStock ? '2px solid #f44336' : '1px solid',
              borderColor: lowStock ? '#f44336' : 'divider',
              backgroundColor: lowStock ? '#ffebee' : 'background.paper'
            }}
          >
            {(inStore || taken) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 2
                }}
              >
                {renderPBadge('card', badgeTooltip, badgeAria)}
              </Box>
            )}
            {item.imageUrl && (
              <Box
                sx={{
                  width: '100%',
                  height: 180,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0
                }}
              >
                <Box
                  component="img"
                  src={`${API_BASE_URL}${item.imageUrl}`}
                  alt={item.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </Box>
            )}
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 'bold',
                    color: lowStock ? '#d32f2f' : '#212121'
                  }}
                >
                  {item.name}
                </Typography>
                {lowStock && (
                  <Tooltip title="Reikia papildyti">
                    <WarningIcon color="error" />
                  </Tooltip>
                )}
              </Box>

              {item.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {item.description}
                </Typography>
              )}

              <Box sx={{ mb: 2 }}>
                {showStoreQuantity ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Sandėlyje:</strong> {item.quantity} {item.unit}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        ...(inStore
                          ? {
                              backgroundColor: '#f3e5f5',
                              color: '#6a1b9a',
                              fontWeight: 600,
                              border: '1px solid',
                              borderColor: '#ce93d8'
                            }
                          : {})
                      }}
                    >
                      <strong>Parduotuvėje:</strong> {item.storeQuantity ?? 0} {item.unit}
                    </Typography>
                  </>
                ) : showTakenQuantity ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Sandėlyje:</strong> {item.quantity} {item.unit}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        ...(taken
                          ? {
                              backgroundColor: '#f3e5f5',
                              color: '#6a1b9a',
                              fontWeight: 600,
                              border: '1px solid',
                              borderColor: '#ce93d8'
                            }
                          : {})
                      }}
                    >
                      <strong>Paimta:</strong> {item.takenQuantity ?? 0} {item.unit}
                    </Typography>
                  </>
                ) : (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Kiekis:</strong> {item.quantity} {item.unit}
                </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Min. kiekis:</strong> {item.minQuantity} {item.unit}
                </Typography>
                {item.location && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Vieta:</strong> {item.location}
                  </Typography>
                )}
                {item.supplier && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Tiekėjas:</strong> {item.supplier}
                  </Typography>
                )}
                {item.prekesKodas && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Prekės kodas:</strong> <code>{item.prekesKodas}</code>
                  </Typography>
                )}
                {item.fabisKodas && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Fabis kodas:</strong> <code>{item.fabisKodas}</code>
                  </Typography>
                )}
                {item.barkodas && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Barkodas:</strong> <code>{item.barkodas}</code>
                  </Typography>
                )}
                {item.creator && (
                  <Chip
                    label={`${item.creator.firstName} ${item.creator.lastName}`}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                <TextField
                  type="number"
                  size="small"
                  label="Kiekis"
                  value={selectedQuantities[item.id] || 1}
                  onChange={(e) => onQuantityChange(item.id, parseInt(e.target.value) || 1)}
                  inputProps={{
                    min: 1,
                    max: getCartMax(item),
                    style: { textAlign: 'center' }
                  }}
                  sx={{ width: 80 }}
                />

                <Button
                  variant="contained"
                  startIcon={<AddToCartIcon />}
                  onClick={() => onAddToCart(item)}
                  disabled={getCartDisabled(item)}
                  sx={{
                    backgroundColor: '#7b1fa2',
                    '&:hover': { backgroundColor: '#6a1b9a' },
                    flex: 1
                  }}
                >
                  Į krepšelį
                </Button>

                {canEdit && (
                  <>
                    <Tooltip title="Redaguoti prekę ir kiekį">
                      <IconButton
                        onClick={() => onEdit(item)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.50' }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ištrinti prekę">
                      <IconButton
                        onClick={() => onDelete(item)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.50' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </CardActions>
          </Card>
        );
      })}
    </Box>
  );
}

export default StorageItemView;
